// @ts-check
// Rotas REST das integrações (docs/arquitetura-appcrm/08-contrato-leads-whatsapp.md): chaves de API, webhooks de saída,
// formulários de captação, Meta Lead Ads, modelos do WhatsApp e o "Conectar com a Meta".
import { api } from './api-client.js';

/**
 * @typedef {{ id: string, name: string, tokenPrefix: string, role: 'ADMIN' | 'MEMBER', expiresAt: string | null, lastUsedAt: string | null,
 *   revokedAt: string | null, createdAt: string }} ApiKey
 * @typedef {{ id: string, targetUrl: string, operations: string[], description: string | null, secret?: string, isActive: boolean,
 *   createdAt: string, updatedAt: string }} Webhook
 * @typedef {{ id: string, webhookId: string, eventName: string, recordId: string | null, status: string, attemptCount: number,
 *   lastStatusCode: number | null, lastError: string | null, deliveredAt: string | null, createdAt: string, payload: any }} WebhookDelivery
 * @typedef {{ enabled: boolean, required: boolean }} FormFieldSetting
 * @typedef {{ title: string, description: string, submitLabel: string, successMessage: string, fields: Record<string, FormFieldSetting>,
 *   maxStudents: number, campusId: string | null, courseId: string | null, campaignId: string | null, ownerWorkspaceMemberId: string | null,
 *   leadSource: string | null, consentText: string, privacyNoticeUrl: string | null, privacyNoticeVersion: string, requireTurnstile: boolean,
 *   redirectUrl: string | null }} CaptureFormSettings
 * @typedef {{ id: string, name: string, isActive: boolean, settings: CaptureFormSettings, submissionCount: number, lastSubmissionAt: string | null,
 *   publicUrl: string, embedSnippet: string, iframeSnippet: string, createdAt: string, updatedAt: string }} CaptureForm
 * @typedef {{ id: string, origin: string, externalId: string | null, payload: any, personId: string | null, householdId: string | null,
 *   enrollmentOpportunityId: string | null, isExistingPerson: boolean, isExistingOpportunity: boolean, createdAt: string }} CaptureSubmission
 * @typedef {{ pageId: string, pageName: string, captureFormId: string | null, createdAt: string, updatedAt: string }} LeadAdsPage
 * @typedef {{ id: string, channelId: string, externalId: string | null, name: string, language: string, category: string | null, status: string,
 *   parameterFormat: string, rejectedReason: string | null, headerFormat: string | null, headerText: string | null, bodyText: string,
 *   footerText: string | null, headerVariables: string[], bodyVariables: string[], isSendable: boolean, buttons?: any[], syncedAt?: string | null,
 *   updatedAt?: string | null, wabaId?: string | null, usage?: 'CAMPAIGN' | 'CONVERSATION' | null, effectiveUsage?: 'CAMPAIGN' | 'CONVERSATION',
 *   group?: 'MARKETING' | 'ANNOUNCEMENT' | 'SERVICE' | 'OTHER', qualityScore?: string | null, qualityUpdatedAt?: string | null,
 *   previousCategory?: string | null, categoryChangedAt?: string | null, correctCategory?: string | null, categoryChangeAt?: string | null,
 *   hasOptOutButton?: boolean, optOutButtonIndex?: number | null, useCount?: number, components?: any[] }} WhatsappTemplate
 * @typedef {{ scope: 'PHONE' | 'PORTFOLIO', id: string, code: number | null, minimumUntil: string, setAt: string }} WhatsappSendingHold
 * @typedef {{ portfolio: { labelSuffix: string, isPortfolioConfirmed: boolean, tier: string | null, limit: number | null, isLimitConfirmed: boolean,
 *   usedLast24h: number, cap: number | null, flowCap: number | null, availableNow: number | null, nextSlotsAt: string | null, checkedAt: string | null },
 *   holds: { phone: WhatsappSendingHold | null, portfolio: WhatsappSendingHold | null }, canReleaseHolds: boolean }} WhatsappMessagingLimit
 */

/** @param {any} body @param {string} key */
const readData = (body, key) => body?.data?.[key];

// Chaves de API

/** @returns {Promise<ApiKey[]>} */
export async function listApiKeys() {
  return readData(await api.get('/rest/api-keys'), 'apiKeys') ?? [];
}

/**
 * @param {{ name: string, role: 'ADMIN' | 'MEMBER', expiresInDays?: number | null, expiresAt?: string | null }} input
 * @returns {Promise<{ apiKey: ApiKey, token: string }>}
 */
export async function createApiKey(input) {
  const body = await api.post('/rest/api-keys', input);
  return body.data;
}

/** @param {string} apiKeyId */
export async function revokeApiKey(apiKeyId) {
  return readData(await api.delete(`/rest/api-keys/${apiKeyId}`), 'apiKey');
}

/**
 * Nova chave no lugar desta (mesmo nome, papel e vencimento); a antiga para de valer. O token só vem aqui.
 * @param {string} apiKeyId @returns {Promise<{ apiKey: ApiKey, token: string, revokedApiKeyId: string }>}
 */
export async function regenerateApiKey(apiKeyId) {
  const body = await api.post(`/rest/api-keys/${apiKeyId}/regenerate`, {});
  return body.data;
}

/** @param {string} apiKeyId @param {{ name?: string, expiresAt?: string | null }} patch @returns {Promise<ApiKey>} */
export async function updateApiKey(apiKeyId, patch) {
  return readData(await api.patch(`/rest/api-keys/${apiKeyId}`, patch), 'apiKey');
}

// Documentação da API (OpenAPI gerada dos metadados da escola, contrato 08 da API)

/** @param {'core' | 'metadata'} [schema] @returns {Promise<any>} */
export async function fetchOpenApiSpec(schema = 'core') {
  return api.get(`/rest/open-api/${schema}`);
}

// Webhooks de saída

/** @returns {Promise<Webhook[]>} */
export async function listWebhooks() {
  return readData(await api.get('/rest/webhooks'), 'webhooks') ?? [];
}

/** @param {string} webhookId @returns {Promise<Webhook>} */
export async function getWebhook(webhookId) {
  return readData(await api.get(`/rest/webhooks/${webhookId}`), 'webhook');
}

/** @param {{ targetUrl: string, operations: string[], description?: string }} input @returns {Promise<Webhook>} */
export async function createWebhook(input) {
  return readData(await api.post('/rest/webhooks', input), 'webhook');
}

/** @param {string} webhookId @param {Partial<Webhook> & { regenerateSecret?: boolean }} patch @returns {Promise<Webhook>} */
export async function updateWebhook(webhookId, patch) {
  return readData(await api.patch(`/rest/webhooks/${webhookId}`, patch), 'webhook');
}

/** @param {string} webhookId */
export async function deleteWebhook(webhookId) {
  return api.delete(`/rest/webhooks/${webhookId}`);
}

/** @param {string} webhookId @returns {Promise<WebhookDelivery[]>} */
export async function listWebhookDeliveries(webhookId) {
  return readData(await api.get(`/rest/webhooks/${webhookId}/deliveries`, { limit: 50 }), 'deliveries') ?? [];
}

/** @param {string} webhookId @returns {Promise<{ success: boolean, statusCode: number | null, error: string | null }>} */
export async function testWebhook(webhookId) {
  const body = await api.post(`/rest/webhooks/${webhookId}/test`, {});
  return body.data;
}

// Formulários de captação

/** @returns {Promise<CaptureForm[]>} */
export async function listCaptureForms() {
  return readData(await api.get('/rest/capture-forms'), 'captureForms') ?? [];
}

/** @param {string} formId @returns {Promise<CaptureForm>} */
export async function getCaptureForm(formId) {
  return readData(await api.get(`/rest/capture-forms/${formId}`), 'captureForm');
}

/** @param {{ name: string, settings?: Partial<CaptureFormSettings> }} input @returns {Promise<CaptureForm>} */
export async function createCaptureForm(input) {
  return readData(await api.post('/rest/capture-forms', input), 'captureForm');
}

/** @param {string} formId @param {{ name?: string, isActive?: boolean, settings?: Partial<CaptureFormSettings> }} patch @returns {Promise<CaptureForm>} */
export async function updateCaptureForm(formId, patch) {
  return readData(await api.patch(`/rest/capture-forms/${formId}`, patch), 'captureForm');
}

/** @param {string} formId */
export async function deleteCaptureForm(formId) {
  return api.delete(`/rest/capture-forms/${formId}`);
}

/** @param {string} formId @returns {Promise<CaptureSubmission[]>} */
export async function listCaptureSubmissions(formId) {
  return readData(await api.get(`/rest/capture-forms/${formId}/submissions`, { limit: 30 }), 'submissions') ?? [];
}

// Meta Lead Ads

/** @returns {Promise<{ pages: LeadAdsPage[], webhookPath: string }>} */
export async function listLeadAdsPages() {
  const body = await api.get('/rest/lead-ads/pages');
  return { pages: body?.data?.pages ?? [], webhookPath: body?.data?.webhookPath ?? '/webhooks/meta-leads' };
}

/** @param {{ pageId: string, accessToken: string, captureFormId: string | null }} input @returns {Promise<LeadAdsPage>} */
export async function connectLeadAdsPage(input) {
  return readData(await api.post('/rest/lead-ads/pages', input), 'page');
}

/** @param {string} pageId @param {string | null} captureFormId @returns {Promise<LeadAdsPage>} */
export async function updateLeadAdsPage(pageId, captureFormId) {
  return readData(await api.patch(`/rest/lead-ads/pages/${pageId}`, { captureFormId }), 'page');
}

/** @param {string} pageId */
export async function disconnectLeadAdsPage(pageId) {
  return api.delete(`/rest/lead-ads/pages/${pageId}`);
}

// Modelos do WhatsApp

/**
 * @param {{ channelId?: string | null, status?: string | null, category?: string | null, usage?: string | null, withUses?: boolean }} [query]
 * @returns {Promise<WhatsappTemplate[]>}
 */
export async function listWhatsappTemplates(query = {}) {
  return readData(await api.get('/rest/whatsapp-templates', {
    channelId: query.channelId ?? undefined, status: query.status ?? undefined, category: query.category ?? undefined, usage: query.usage ?? undefined,
    withUses: query.withUses ? 'true' : undefined,
  }), 'templates') ?? [];
}

/**
 * Página do modelo (2.0.0): o modelo, as cópias da mesma conta e as campanhas e fluxos ativos que usam.
 * @param {string} templateId
 * @returns {Promise<{ template: WhatsappTemplate, copies: Array<{ id: string, channelId: string, status: string }>,
 *   uses: { campaigns: Array<{ id: string, name: string }>, flows: Array<{ id: string, name: string }> } }>}
 */
export async function getWhatsappTemplateDetail(templateId) {
  return (await api.get(`/rest/whatsapp-templates/${encodeURIComponent(templateId)}`))?.data;
}

/**
 * Onde o modelo aparece (2.0.0): `CAMPAIGN` (comunicados), `CONVERSATION` (atendimento) ou null (pela categoria).
 * @param {string} templateId @param {'CAMPAIGN' | 'CONVERSATION' | null} usage @returns {Promise<WhatsappTemplate>}
 */
export async function updateWhatsappTemplateUsage(templateId, usage) {
  return readData(await api.patch(`/rest/whatsapp-templates/${templateId}`, { usage }), 'template');
}

/** Limite diário da Meta e retenções do número (2.0.0). @param {string} channelId @returns {Promise<WhatsappMessagingLimit>} */
export async function getWhatsappMessagingLimit(channelId) {
  return (await api.get(`/rest/channels/${channelId}/whatsapp/messaging-limit`))?.data;
}

/** Libera a retenção da Meta depois do prazo mínimo (2.0.0). @param {string} channelId @param {'PHONE' | 'PORTFOLIO'} scope */
export async function releaseWhatsappSendingHold(channelId, scope) {
  return readData(await api.post(`/rest/channels/${channelId}/whatsapp/sending-hold/release`, { scope, acknowledge: true }), 'released');
}

/** @param {string | null} [channelId] @returns {Promise<WhatsappTemplate[]>} */
export async function syncWhatsappTemplates(channelId) {
  return readData(await api.post('/rest/whatsapp-templates/sync', { channelId: channelId ?? undefined }), 'templates') ?? [];
}

/**
 * @param {{ channelId?: string | null, name: string, language: string, category: string, headerText?: string, bodyText: string, footerText?: string, examples: string[],
 *   usage?: 'CAMPAIGN' | 'CONVERSATION' | null, optOutButton?: boolean, optOutButtonText?: string }} input
 *   usage, optOutButton e optOutButtonText: modelos de campanha (2.0.0, contrato 09 de campanhas de WhatsApp, seção 2)
 * @returns {Promise<WhatsappTemplate>}
 */
export async function createWhatsappTemplate(input) {
  return readData(await api.post('/rest/whatsapp-templates', input), 'template');
}

/** @param {string} templateId */
export async function deleteWhatsappTemplate(templateId) {
  return api.delete(`/rest/whatsapp-templates/${templateId}`);
}

/**
 * @param {string} threadId @param {{ templateId: string, variables: Record<string, string>, headerVariables?: Record<string, string> }} input
 */
export async function sendTemplateMessage(threadId, input) {
  return readData(await api.post(`/rest/conversations/${threadId}/template-messages`, input), 'message');
}

// Conectar com a Meta pelo portal (contrato 08, seção 8.1)

/**
 * Passe curto para o popup do portal; o endereço já traz o passe no fragmento. A prova fica só em memória e vai ao
 * popup no aperto de mão (contrato 08, seção 8.1, passo 3).
 * @param {string} attemptId @returns {Promise<{ connectUrl: string, portalOrigin: string, expiresAt: string, proof: string }>}
 */
export async function requestMetaSignupPass(attemptId) {
  return (await api.post('/rest/channels/whatsapp/meta-signup-pass', { attemptId })).data;
}

/** "Registrar de novo": o register da Cloud API com o PIN guardado. @param {string} channelId */
export async function registerWhatsappNumber(channelId) {
  return readData(await api.post(`/rest/channels/${channelId}/whatsapp/register`, {}), 'channel');
}

/**
 * Preenche as variáveis do texto do modelo para a prévia.
 * @param {string | null | undefined} text @param {Record<string, string>} values
 */
export function renderTemplateText(text, values) {
  return String(text ?? '').replace(/\{\{\s*(\w+)\s*\}\}/g, (match, name) => (values[name] ? values[name] : match));
}
